import React, { useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import {
  FacebookShareButton,
  TwitterShareButton,
  TelegramShareButton,
  FacebookMessengerShareButton,
  WhatsappShareButton,
} from 'react-share';
import BarFacebookIcon from 'images/Icons/BarFacebookIcon';
import BarTwitterIcon from 'images/Icons/BarTwitterIcon';
import BarTelegramIcon from 'images/Icons/BarTelegramIcon';
import BarMessengerIcon from 'images/Icons/BarMessengerIcon';
import BarWhatsappIcon from 'images/Icons/BarWhatsappIcon';
import SocialsIcon from 'images/Icons/SocialsIcon';
import styled, { theme } from 'styles/styled';
import { useLocation } from 'hooks/useLocation';
import useToggle from 'hooks/useToggle';
import useOuterClick from 'hooks/useOuterClick';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: fixed;
  bottom: -5px;
  right: 0;
  height: auto;
  width: auto;
  z-index: 10;
  cursor: pointer;

  @media ${theme.devices.medium} {
    bottom: 20px;
    right: 20px;
  }
`;

const ReactIconWrapper = styled.div`
  width: 32px;
  height: 32px;
  padding-bottom: 5px;
`;

const SocialsIconWrapper = styled.div`
  z-index: 11;
`;

const IconsWrapper = styled.div<{ showSocials: boolean }>`
  height: ${({ showSocials }) => (showSocials ? '215px' : '0')};
  width: 40px;
  background-color: ${theme.palette.light};
  box-shadow: 0 2px 11px 1px rgba(163, 193, 214, 0.53);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  border-radius: 20px;
  position: relative;
  bottom: 5px;
  transition: height 0.4s ease;
  overflow: ${({ showSocials }) => (showSocials ? 'unset' : 'hidden')};

  ${ReactIconWrapper} {
    transition: opacity 0.6s ease;
    opacity: ${({ showSocials }) => (showSocials ? '1' : '0')};
  }

  svg {
    transition: height 0.5s ease;
    height: ${({ showSocials }) => (showSocials ? '100%' : '0')};
  }

  &:after {
    content: '';
    display: ${({ showSocials }) => (showSocials ? 'flex' : 'none')};
    position: absolute;
    bottom: -5px;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;

    border-top: 10px solid ${theme.palette.light};
  }
`;

interface BarProps {
  pageTitle?: string;
  show?: boolean;
}

const SocialShareBar = ({ pageTitle, show = true }: BarProps) => {
  const { on: showSocials, toggle: setShowSocials, setOff } = useToggle(false);

  const socialShareBarRef = useRef(null);
  useOuterClick(socialShareBarRef, setOff);

  const data = useStaticQuery(graphql`
    query HeaderQuery {
      site {
        siteMetadata {
          title
        }
      }
    }
  `);
  const location = useLocation();
  const title = pageTitle || data.site.siteMetadata.title;
  const url = location.href;
  if (!show) return null;

  return (
    <Wrapper ref={socialShareBarRef}>
      <IconsWrapper showSocials={showSocials}>
        <FacebookShareButton url={url} title={title}>
          <ReactIconWrapper
            style={{
              left: '-5px',
              position: 'relative',
            }}>
            <BarFacebookIcon />
          </ReactIconWrapper>
        </FacebookShareButton>
        <TwitterShareButton url={url} title={title}>
          <ReactIconWrapper>
            <BarTwitterIcon />
          </ReactIconWrapper>
        </TwitterShareButton>
        <TelegramShareButton url={url} title={title}>
          <ReactIconWrapper>
            <BarTelegramIcon />
          </ReactIconWrapper>
        </TelegramShareButton>
        <FacebookMessengerShareButton
          url={url}
          appId={process.env.MESSENGER_APP_ID || ''}
          title={title}>
          <BarMessengerIcon />
        </FacebookMessengerShareButton>
        <WhatsappShareButton url={url} title={title}>
          <BarWhatsappIcon />
        </WhatsappShareButton>
      </IconsWrapper>
      <SocialsIconWrapper onClick={() => setShowSocials()}>
        <SocialsIcon />
      </SocialsIconWrapper>
    </Wrapper>
  );
};

export default SocialShareBar;
